/**
 * The side-by-side compare of a saved run ticket and a parked second one (OPEN-144 §2c).
 * Differing rows are highlighted. Plain words for the three things a reviewer must know:
 * the saved ticket changed after the park; the driver may have printed the parked ticket;
 * Extra Services are not swapped (fixed in billing). Photos that arrived after the park are
 * marked "may belong to either ticket" (§11).
 */

import React from 'react';
import { compareRows, extraServicesDiffer } from './compare-rows';
import type { SyncConflictDetailView } from './use-sync-conflicts';

const NOTE: React.CSSProperties = {
  fontSize: '13px',
  color: 'var(--color-text-secondary)',
  margin: '6px 0',
};
const WARN: React.CSSProperties = {
  ...NOTE,
  color: 'var(--color-warning)',
  fontWeight: 600,
};
const CELL: React.CSSProperties = {
  padding: '4px 8px',
  borderBottom: '1px solid var(--color-border-secondary)',
  fontSize: '13px',
  verticalAlign: 'top',
};

export function ParkedCompare({
  compare,
  timeZone,
}: {
  compare: NonNullable<SyncConflictDetailView['compare']>;
  timeZone: string;
}): React.JSX.Element {
  const rows = compareRows(compare.saved, compare.parked, timeZone);
  const photos = compare.photos;
  return (
    <div>
      {compare.savedChangedSinceParked && (
        <p role="note" style={WARN}>
          The saved ticket was corrected after this one was parked. Keep the saved ticket, or Send
          Back.
        </p>
      )}
      {compare.saved === null && (
        <p role="note" style={WARN}>
          This job no longer has a saved ticket.
        </p>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Compare tickets">
        <thead>
          <tr>
            <th style={{ ...CELL, textAlign: 'left' }}>Field</th>
            <th style={{ ...CELL, textAlign: 'left' }}>Saved ticket</th>
            <th style={{ ...CELL, textAlign: 'left' }}>Parked ticket</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={`${r.section}-${r.label}`}
              data-differs={r.differs ? 'true' : undefined}
              style={
                r.differs
                  ? { backgroundColor: 'rgba(246, 182, 94,0.15)', fontWeight: 600 }
                  : undefined
              }
            >
              <td style={CELL}>{r.label}</td>
              <td style={CELL}>{r.saved}</td>
              <td style={CELL}>{r.parked}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {extraServicesDiffer(rows) && (
        <p style={NOTE}>
          Extra Services stay as on the saved ticket either way. Fix any difference in billing.
        </p>
      )}
      {photos.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>Photos on this job</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0' }}>
            {photos.map((ph, i) => (
              <li key={ph.key} style={{ fontSize: '13px', padding: '2px 0' }}>
                {ph.url !== null ? (
                  <a
                    href={ph.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-accent-blue)' }}
                  >
                    Photo {i + 1}
                  </a>
                ) : (
                  <span>Photo {i + 1} (unavailable)</span>
                )}
                {ph.arrivedAfterPark && (
                  <span style={{ marginLeft: '8px', color: 'var(--color-text-tertiary)' }}>
                    Arrived after the second ticket was parked; may belong to either ticket.
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p style={NOTE}>The driver’s phone may have printed this second ticket.</p>
    </div>
  );
}
